// Types mirroring the backend DTOs (com.mydailydirect.models.*). Money is integer paise.
// Kept hand-authored and small so the pilot has no codegen toolchain; a backend field
// change is a one-line edit here that flows to all 5 apps. Structure matches the DTOs
// 1:1 so this can later be swapped for an OpenAPI-generated client without app changes.

// ---- identity ----
export type RoleType =
  | "TRADER"
  | "RETAILER"
  | "RM"
  | "DRIVER"
  | "ADMIN"
  | "SUPER_ADMIN"
  | "VENDOR";

export interface RetailerDTO {
  id: number | null;
  name: string;
  phone?: string | null;
  city?: string | null;
  addressLine?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  gstNumber?: string | null;
  photoUrl?: string | null;
  status?: string | null;
  contactPerson?: string | null;
  businessType?: string | null;
  onboardingSource?: "FIELD_MM" | "SELF_SIGNUP" | "ADMIN_PUNCH" | "WHATSAPP_BOT" | string | null;
  onboardedBy?: string | null;
  onboardedByPhone?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  gstExempt?: boolean | null;
  kycBypassed?: boolean | null;
  kycBypassedBy?: string | null;
  kycBypassReason?: string | null;
  complianceTier?: "GST_REGISTERED" | "SMALL_KIRANA" | string | null;
  fssaiNumber?: string | null;
  fssaiCertificateUrl?: string | null;
  panNumber?: string | null;
  kycUpdatedAt?: string | null;
  kycUpdatedBy?: string | null;
}

export function isSmallKiranaOrExempt(r?: RetailerDTO | null): boolean {
  if (!r) return false;
  if (r.gstExempt || r.kycBypassed) return true;
  if (r.complianceTier) {
    const ct = r.complianceTier.toUpperCase();
    if (
      ct === "SMALL_KIRANA" ||
      ct === "RESTAURANT_EATERY" ||
      ct === "MICRO_RETAILER" ||
      ct === "GST_EXEMPT" ||
      ct === "NON_GST" ||
      ct === "EXEMPT" ||
      ct === "COMPOSITION"
    ) {
      return true;
    }
  }
  if (r.businessType) {
    const bt = r.businessType.toLowerCase();
    return (
      bt.includes("kirana") ||
      bt.includes("micro") ||
      bt.includes("provision") ||
      bt.includes("petty") ||
      bt.includes("small") ||
      bt.includes("unregistered") ||
      bt.includes("restaurant") ||
      bt.includes("hotel") ||
      bt.includes("eatery") ||
      bt.includes("dhaba") ||
      bt.includes("cafe") ||
      bt.includes("bakery") ||
      bt.includes("cater") ||
      bt.includes("tea") ||
      bt.includes("food")
    );
  }
  return false;
}

export interface UserAccountDTO {
  id: number | null;
  username: string;
  fullName?: string | null;
  role: RoleType;
  retailerId?: number | null;
  vendorId?: number | null;
  phone?: string | null;
  email?: string | null;
  accountStatus?: string | null;
  password?: string | null;
}

// ---- catalog ----
export interface SkuDTO {
  id: number | null;
  code: string;
  name: string;
  unit?: string | null;
  category?: string | null;
  pricePaise: number;
  nipPaise?: number | null;
  weightKg?: number | null;
  mrpPaise?: number | null;
  marginPercent?: number | null;
  gstPercent?: number | null;
  availableQty: number;
  active: boolean;
  approvalStatus?: string | null;
  photoUrls?: string[] | null;
  vendorId?: number | null;
  vendorName?: string | null;
  brand?: string | null;
  hsnCode?: string | null;
  minOrderQty?: number | null;
  description?: string | null;
  origin?: string | null;
  packagingType?: string | null;
  shelfLife?: string | null;
  storageInstructions?: string | null;
  moq?: number | null;
  qpsTier1Qty?: number | null;
  qpsTier1DiscountPercent?: number | null;
  qpsTier2Qty?: number | null;
  qpsTier2DiscountPercent?: number | null;
  /** Assortments (pack sizes). Empty/absent when the SKU is sold as a single unit. */
  packs?: SkuPackDTO[] | null;
  pricingVertical?: string | null;
  measurementUnit?: string | null;
  baselineMarginPaise?: number | null;
  baselineLogisticsPaise?: number | null;
  baselineSellingPricePaise?: number | null;
  actualMarginPaise?: number | null;
  adminPriceOverride?: boolean | null;
}

/** Input type for creating or bulk importing SKUs where code is auto-generated if omitted. */
export type CreateSkuDTO = Omit<SkuDTO, "id" | "code"> & {
  id?: number | null;
  code?: string | null;
};

/** An assortment of a SKU, e.g. "Pack of 10" or "25 kg". Each assortment is physically an independent SKU with its own skuId/skuCode for orders, stock, and returns. */
export interface SkuPackDTO {
  id?: number | null;
  /** Distinct child variant SKU ID for warehouse barcode scan, order tracking, and individual return debit notes. */
  skuId?: number | null;
  /** Distinct variant SKU Code, e.g. "RICE-SM-25K-P1" or "AASH-ATT-10K". */
  skuCode?: string | null;
  /** Barcode / EAN-13 / GTIN for this physical pack variant. */
  barcode?: string | null;
  label: string;
  packSize?: number | null;
  packUnit?: string | null;
  /** Base units consumed from SKU stock per pack sold (if stock is shared). */
  unitsPerPack?: number | null;
  /** Price of ONE pack in paise. */
  pricePaise: number;
  /** Vendor Supply purchase price for ONE pack in paise. */
  supplyPricePaise?: number | null;
  nipPaise?: number | null;
  baselineMarginPaise?: number | null;
  baselineLogisticsPaise?: number | null;
  baselineSellingPricePaise?: number | null;
  actualMarginPaise?: number | null;
  adminPriceOverride?: boolean | null;
  mrpPaise?: number | null;
  weightKg?: number | null;
  /** Dedicated available inventory for this pack size. */
  stock?: number | null;
  isDefault?: boolean | null;
  active?: boolean | null;
  displayOrder?: number | null;
}

// ---- order ----
export type OrderStatus =
  | "PLACED"
  | "CONFIRMED"
  | "LINED"
  | "LOADED"
  | "INVOICED"
  | "DELIVERED"
  | "EXCEPTION"
  | "RETURNED"
  | "RTO"
  | "SETTLED"
  | "CANCELLED";

export interface OrderLineDTO {
  id: number | null;
  skuId: number;
  qty: number;
  unitPricePaise?: number | null;
  lineTotalPaise?: number | null;
  packId?: number | null;
  packLabel?: string | null;
  unitsPerPack?: number | null;
}

export interface OrderDTO {
  id: number;
  retailerId: number;
  status: OrderStatus;
  placedAt: string;
  totalPaise: number;
  lines: OrderLineDTO[];
  paymentMethod?: string;
}

export interface CreateOrderRequest {
  retailerId: number;
  lines: Array<{ skuId: number; qty: number; packId?: number | null }>;
}

export interface TransitionOrderRequest {
  target: OrderStatus;
}

// ---- cart ----
export interface CartItemDTO {
  id: number;
  skuId: number;
  skuName?: string | null;
  skuCode?: string | null;
  qty: number;
  unitPricePaise: number;
  lineTotalPaise: number;
  addedBy?: string | null;
  packId?: number | null;
  packLabel?: string | null;
  unitsPerPack?: number | null;
}

export interface CartDTO {
  id: number;
  retailerId: number;
  items: CartItemDTO[];
  totalPaise: number;
  totalItems: number;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface PushToCartRequest {
  retailerId: number;
  items: Array<{ skuId: number; qty: number; packId?: number | null }>;
  updatedBy?: string;
}

// ---- inventory ----
export type LedgerReason = "STOCK_IN" | "ALLOCATE" | "RETURN" | "ADJUST";

export interface LedgerEntryDTO {
  id: number;
  skuId: number;
  qtyDelta: number;
  reason: LedgerReason;
  refType?: string | null;
  refId?: number | null;
  occurredAt: string;
}

export interface StockLevelDTO {
  skuId: number;
  onHandQty: number;
}

export interface StockAdjustmentRequest {
  skuId: number;
  qtyDelta: number;
  reason: LedgerReason;
}

// ---- dispatch ----
export type RouteStatus = "PLANNED" | "DISPATCHED" | "COMPLETED";
export type StopStatus = "PENDING" | "DELIVERED" | "EXCEPTION";

export interface RouteStopDTO {
  id: number;
  orderId: number;
  bayNo: number;
  slotNo: number;
  sequenceNo: number;
  status: StopStatus;
  podNote?: string | null;
  paymentMethod?: string | null;
  amountCollectedPaise?: number | null;
  revisedTotalPaise?: number | null;
  shortagePaise?: number | null;
}

export interface RouteDTO {
  id: number;
  code: string;
  vehicleReg?: string | null;
  scheduledDate: string;
  status: RouteStatus;
  stops: RouteStopDTO[];
}

export interface CreateRouteRequest {
  code: string;
  vehicleReg?: string;
  scheduledDate: string;
}

export interface AddStopRequest {
  orderId: number;
  bayNo: number;
  slotNo: number;
  sequenceNo: number;
}

export interface DeliveryLineOutcome {
  orderLineId: number;
  skuId: number;
  orderedQty: number;
  deliveredQty: number;
  rejectedQty: number;
  damagedQty: number;
  rejectionReason?: string;
  discountPaise?: number;
  discountReason?: string;
}

export type PaymentMethod = "CASH" | "QR" | "SPLIT" | "MULTI";

export interface StopOutcomeRequest {
  status: StopStatus;
  podNote?: string;
  paymentMethod?: PaymentMethod;
  paymentRef?: string;
  amountCollectedPaise?: number;
  revisedTotalPaise?: number;
  shortagePaise?: number;
  deliveryLines?: DeliveryLineOutcome[];
}

export type SettlementStatus = "DRAFT" | "FINALIZED";

export interface SettlementLineDTO {
  id: number;
  orderId: number;
  retailerId: number;
  grossPaise: number;
}

export interface SettlementRunDTO {
  id: number;
  periodStart: string;
  periodEnd: string;
  status: SettlementStatus;
  totalGrossPaise: number;
  lines: SettlementLineDTO[];
}

export interface CreateSettlementRunRequest {
  periodStart: string;
  periodEnd: string;
  orderIds: number[];
}

// ---- shared problem+json ----
export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
}

// ---- WhatsApp Messaging & AI Voice ----
export interface WhatsAppMessageDTO {
  id: number;
  toPhone?: string;
  recipientPhone?: string;
  retailerId?: number | null;
  direction: "OUTBOUND" | "INBOUND";
  messageType: "TEXT" | "TEMPLATE" | "INTERACTIVE" | "BROADCAST";
  templateName?: string;
  languageCode?: string;
  content?: string;
  payload?: string;
  status: "PENDING" | "SENT" | "DELIVERED" | "READ" | "FAILED";
  externalMessageId?: string;
  errorMessage?: string;
  createdAt: string;
}

export interface WhatsAppOptInDTO {
  id?: number;
  retailerId?: number;
  phone: string;
  optedIn: boolean;
  optInSource?: string;
  languageCode: string;
}

export interface UpdateWhatsAppOptInRequest {
  retailerId: number;
  phone: string;
  optedIn: boolean;
  preferredLanguage: string;
}

export interface SendWhatsAppTestRequest {
  retailerId: number;
  text: string;
}

export interface ParsedWhatsAppLineItem {
  skuId: number;
  skuName: string;
  unit: string;
  qty: number;
  unitPricePaise: number;
  lineTotalPaise: number;
}

export interface ParseWhatsAppOrderRequest {
  retailerId?: number | null;
  phone?: string;
  text: string;
  forceCreate?: boolean;
}

export interface ParseWhatsAppOrderResponse {
  matched: boolean;
  orderCreated: boolean;
  orderId: number | null;
  retailerId: number | null;
  retailerName: string | null;
  totalPaise: number;
  items: ParsedWhatsAppLineItem[];
  replyMessage: string;
}

export interface VoiceCallerContextDTO {
  retailerId: number | null;
  shopName: string;
  contactPerson: string;
  phone: string;
  city: string;
  preferredLanguage: string;
  verified: boolean;
  recentOrderedItems: string[];
  liveCatalogRates: Array<{
    skuId: number;
    name: string;
    unit: string;
    priceRupees: number;
  }>;
  suggestedGreetingKannada: string;
  suggestedGreetingEnglish: string;
}

export interface TopDealItemDTO {
  skuId: number;
  code: string;
  name: string;
  unit: string;
  category: string;
  pricePaise: number;
  mrpPaise: number;
  savingsPaise: number;
  discountPercent: number;
  availableQty: number;
}

export interface TopDealsPreviewResponse {
  topDeals: TopDealItemDTO[];
  totalActiveBuyers: number;
  messageEn: string;
  messageKn: string;
  messageHi: string;
}

export interface BroadcastTopDealsRequest {
  skuIds?: number[];
  limit?: number;
  customText?: string;
}

export interface BroadcastTopDealsResponse {
  totalBuyers: number;
  sentCount: number;
  skippedCount: number;
  topDeals: TopDealItemDTO[];
  messageEn: string;
  messageKn: string;
  messageHi: string;
}

export interface DailyBroadcastPreviewResponse {
  totalActiveBuyers: number;
  returningBuyersWithOrders: number;
  newBuyersWithoutOrders: number;
  featuredSkus: TopDealItemDTO[];
  sampleReturningBuyerName: string;
  sampleReturningBuyerMessage: string;
  sampleNewBuyerMessage: string;
}

export interface DailyBroadcastResultResponse {
  totalBuyers: number;
  sentReturningCount: number;
  sentNewBuyerCount: number;
  skippedCount: number;
  featuredSkus: TopDealItemDTO[];
}

export interface InteractiveFlowStepResponse {
  retailerId: number;
  retailerName: string;
  action: string;
  replyMessage: string;
  cartItems: ParsedWhatsAppLineItem[];
  cartTotalPaise: number;
  orderCreated: boolean;
  orderId: number | null;
}

export interface WhatsAppConfigDTO {
  provider: string;
  isMetaConfigured: boolean;
  phoneNumberId: string;
  wabaId: string;
  businessPhone: string;
  webhookUrl: string;
  verifyToken: string;
}

export interface UpdateWhatsAppConfigRequest {
  provider?: string;
  phoneNumberId?: string;
  accessToken?: string;
  wabaId?: string;
  verifyToken?: string;
}

// ---- analytics ----
export interface AnalyticsEventDTO {
  id?: number | null;
  eventName: string;
  appName?: string;
  sessionId: string;
  anonymousId?: string;
  userId?: string | null;
  retailerId?: number | null;
  vendorId?: number | null;
  pagePath?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  deviceType?: string;
  skuId?: number | null;
  orderId?: number | null;
  searchQuery?: string | null;
  valuePaise?: number | null;
  propsJson?: string | null;
  occurredAt?: string;
}

export interface FunnelStepDTO {
  stepKey: string;
  label: string;
  sessions: number;
  conversionFromStartPercent: number;
  dropOffFromPrevPercent: number;
}

export interface TrafficSourceDTO {
  source: string;
  sessions: number;
  orders: number;
  conversionPercent: number;
}

export interface AppTrafficDTO {
  appName: string;
  sessions: number;
  events: number;
  uniqueUsers: number;
}

export interface TopProductMetricDTO {
  skuId: number;
  skuName: string;
  views: number;
  cartAdds: number;
  orders: number;
  viewToCartPercent: number;
}

export interface TopSearchMetricDTO {
  query: string;
  searches: number;
  zeroResultCount: number;
}

export interface DailyMetricPointDTO {
  date: string;
  sessions: number;
  pageViews: number;
  addToCarts: number;
  checkouts: number;
  orders: number;
}

export interface AnalyticsDashboardDTO {
  uniqueVisitors: number;
  totalSessions: number;
  totalPageViews: number;
  homePageSessions: number;
  productViewSessions: number;
  addToCartSessions: number;
  checkoutStartedSessions: number;
  orderPlacedSessions: number;
  totalOrdersPlaced: number;
  totalRevenuePaise: number;
  overallConversionPercent: number;
  funnel: FunnelStepDTO[];
  trafficSources: TrafficSourceDTO[];
  appBreakdown: AppTrafficDTO[];
  topProducts: TopProductMetricDTO[];
  topSearches: TopSearchMetricDTO[];
  dailyTrend: DailyMetricPointDTO[];
  recentEvents: AnalyticsEventDTO[];
}

// ---- vendor ----
export interface VendorDTO {
  id?: number | null;
  legalName: string;
  tradeName?: string | null;
  businessType?: string | null;
  categories?: string | null;
  contactName?: string | null;
  phone: string;
  email?: string | null;
  alternatePhone?: string | null;
  authorizedPhones?: string | null;
  gstNumber?: string | null;
  panNumber?: string | null;
  fssaiNumber?: string | null;
  fssaiExpiry?: string | null;
  msmeNumber?: string | null;
  registeredAddress?: string | null;
  warehouseAddress?: string | null;
  city?: string | null;
  pincode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  bankAccountHolder?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  bankName?: string | null;
  cancelledChequeUrl?: string | null;
  gstCertificateUrl?: string | null;
  panCardUrl?: string | null;
  fssaiCertificateUrl?: string | null;
  shopPhotoUrl?: string | null;
  commissionPercent?: number | null;
  payoutCycle?: string | null;
  minOrderValuePaise?: number | null;
  leadTimeHours?: number | null;
  status?: string | null;
  reviewerNote?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  createdAt?: string | null;
}

// ---- sample request ----
export interface SampleRequestDTO {
  id: number;
  kitType: string;
  businessName: string;
  contactPerson: string;
  phone: string;
  address: string;
  deliverySlot: string;
  status: string;
  notes?: string | null;
  createdAt: string;
  gstNumber?: string | null;
  docUrl?: string | null;
  gstinVerified?: boolean | null;
}

export interface CreateSampleRequest {
  kitType: string;
  businessName: string;
  contactPerson: string;
  phone: string;
  address: string;
  deliverySlot?: string;
  notes?: string;
  gstNumber?: string | null;
  docUrl?: string | null;
}

export interface UpdateSampleRequestStatus {
  status: string;
  notes?: string;
}

// ---- vendor SKU confirmations ----
export interface VendorSkuConfirmationDTO {
  id?: number | null;
  vendorId: number;
  skuId: number;
  fulfillmentDate: string; // YYYY-MM-DD
  requiredQty: number;
  confirmedQty: number;
  shortageQty: number;
  status: string; // PENDING | CONFIRMED | SHORTAGE | REJECTED
  shortageReason?: string | null;
  notes?: string | null;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---- vendor returns & QC inspection desk ----
export interface VendorReturnDTO {
  id?: number | null;
  vendorId: number;
  returnBatchNumber: string;
  orderNumber?: string | null;
  pickupDate?: string | null;
  returnDate: string; // YYYY-MM-DD
  skuId: number;
  skuCode?: string | null;
  skuName?: string | null;
  assortmentPackLabel?: string | null;
  assortmentSkuId?: string | null;
  unit?: string | null;
  dispatchedQty: number;
  returnedQty: number;
  unitSupplyPricePaise: number;
  totalReturnAmountPaise: number;
  reasonCode: string;
  reasonLabel?: string | null;
  qcInspectorNote?: string | null;
  status: string; // HANDOVER_PENDING | COLLECTED_BY_VENDOR | DISPUTE_RAISED | DEBIT_NOTE_SETTLED
  hubLocation?: string | null;
  gatePassNumber?: string | null;
  gatePassIssuedAt?: string | null;
  collectedAt?: string | null;
  collectedBy?: string | null;
  disputeReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---- vendor payouts & settlement ledger (Section 194Q TDS) ----
export interface VendorPayoutDTO {
  id?: number | null;
  vendorId: number;
  payoutNumber: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string; // YYYY-MM-DD
  payoutDate: string; // YYYY-MM-DD
  grossAmountPaise: number;
  returnsDeductionPaise: number;
  tdsDeductionPaise: number;
  platformFeePaise: number;
  netPayoutAmountPaise: number;
  utrNumber?: string | null;
  bankAccountMasked?: string | null;
  status: string; // SCHEDULED | IN_TRANSIT | PROCESSED
  ordersCount: number;
  billedUnits: number;
  returnedUnits: number;
  processedAt?: string | null;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---- RM store visits ----
export interface RmVisitDTO {
  id?: number | null;
  retailerId: number;
  retailerName: string;
  rmUserId?: string | null;
  rmName?: string | null;
  visitType: string;
  note?: string | null;
  photoUrl?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  visitedAt?: string | null;
  createdAt?: string | null;
}

// ---- Buyer Focus Package & Price Trend Intelligence ----
export interface PricePointDTO {
  date: string;
  pricePaise: number;
}

export interface BuyerFocusPackageItemDTO {
  id?: number | null;
  packageId?: number | null;
  skuId: number;
  skuCode: string;
  skuName: string;
  category?: string | null;
  unit?: string | null;
  brand?: string | null;
  pricePaise: number;
  mrpPaise?: number | null;
  defaultQty: number;
  targetPricePaise?: number | null;
  notes?: string | null;
  displayOrder?: number;
  minPrice7dPaise: number;
  maxPrice7dPaise: number;
  priceTrend: "LOWEST_7D" | "DOWN" | "UP" | "STABLE" | string;
  trendPercent: number;
  trendLabel: string;
  isLowest7d: boolean;
  history?: PricePointDTO[];
  packs?: SkuPackDTO[] | null;
}

export interface BuyerFocusPackageDTO {
  id?: number | null;
  retailerId: number;
  retailerName: string;
  name: string;
  description?: string | null;
  items: BuyerFocusPackageItemDTO[];
  totalEstimatedPaise: number;
  itemCount: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SaveFocusPackageRequest {
  name?: string;
  description?: string;
  items: {
    skuId: number;
    defaultQty?: number;
    targetPricePaise?: number | null;
    notes?: string | null;
  }[];
}

export interface AddFocusPackageItemRequest {
  skuId: number;
  defaultQty?: number;
  targetPricePaise?: number | null;
  notes?: string | null;
}

export interface BuyerProductRequestDTO {
  id?: number | null;
  retailerId: number;
  retailerName: string;
  productName: string;
  category?: string | null;
  expectedQty?: number | null;
  targetPricePaise?: number | null;
  comments?: string | null;
  status: string;
  createdAt?: string | null;
  createdBy?: string | null;
}

export interface CreateBuyerProductRequest {
  retailerId: number;
  productName: string;
  category?: string | null;
  expectedQty?: number | null;
  targetPricePaise?: number | null;
  comments?: string | null;
}

// ---- competitor price intelligence ----
export type CompetitorPlatform = "UDAAN" | "JUMBOTAIL" | "TRADEFACE";

export interface CompetitorPriceMappingDTO {
  id?: number | null;
  skuId: number;
  platform: CompetitorPlatform;
  competitorSkuId?: string | null;
  competitorProductName?: string | null;
  competitorUrl?: string | null;
  packWeightKg?: number | null;
  currentPricePaise?: number | null;
  mrpPaise?: number | null;
  inStock?: boolean | null;
  matchConfidence?: string | null;
  crawlStatus?: string | null;
  lastErrorMessage?: string | null;
  lastCrawledAt?: string | null;
}

export interface CompetitorAuthSessionDTO {
  id?: number | null;
  platform: CompetitorPlatform;
  phoneNumber?: string | null;
  pincode?: string | null;
  city?: string | null;
  authHeadersJson?: string | null;
  cookiesText?: string | null;
  bearerToken?: string | null;
  refreshToken?: string | null;
  refreshEndpointUrl?: string | null;
  otpSendUrl?: string | null;
  otpVerifyUrl?: string | null;
  apiBaseUrl?: string | null;
  deviceId?: string | null;
  userAgent?: string | null;
  proxyUrl?: string | null;
  minDelayMs?: number | null;
  maxDelayMs?: number | null;
  cacheTtlMinutes?: number | null;
  dailyRequestBudget?: number | null;
  requestsToday?: number | null;
  circuitBreakerUntil?: string | null;
  autoKeepAlive?: boolean | null;
  lastKeepAliveAt?: string | null;
  authenticatedAt?: string | null;
  active: boolean;
  lastCrawlStatus?: string | null;
  lastVerifiedAt?: string | null;
  updatedAt?: string | null;
}

export interface CompetitorOtpSendRequestDTO {
  platform: CompetitorPlatform;
  phoneNumber: string;
  pincode?: string | null;
  city?: string | null;
  otpSendUrl?: string | null;
  deviceId?: string | null;
}

export interface CompetitorOtpVerifyRequestDTO {
  platform: CompetitorPlatform;
  phoneNumber: string;
  otp: string;
  requestId?: string | null;
  otpVerifyUrl?: string | null;
}

export interface CompetitorOtpResponseDTO {
  platform: CompetitorPlatform;
  phoneNumber: string;
  otpSent: boolean;
  verified: boolean;
  requestId?: string | null;
  status: string;
  message: string;
  session: CompetitorAuthSessionDTO;
}

export interface CompetitorCrawlSummaryDTO {
  totalSkusProcessed: number;
  mappingsUpdated: number;
  priceDropsDetected: number;
  cheaperThanOurNipCount: number;
  failedCount: number;
  completedAt: string;
  message: string;
}

export interface VendorNegotiationRequestDTO {
  skuId: number;
  vendorId?: number | null;
  vendorPhone?: string | null;
  targetNipPaise?: number | null;
  customMessage?: string | null;
}

// ---- Super Admin: Vertical Pricing Rule Book & Company Performance ----
export interface PricingRuleSlabDTO {
  id: number;
  vertical: string;
  verticalLabel: string;
  slabLabel: string;
  minQty: number;
  minInclusive: boolean;
  maxQty?: number | null;
  maxInclusive: boolean;
  measurementBasis: "KG" | "LITRE" | "PACK" | string;
  marginCalcType: "PER_KG" | "PER_LITRE" | "PER_PACK" | string;
  marginRatePaise: number;
  logisticsRatePaise: number;
  notes?: string | null;
  displayOrder: number;
  active: boolean;
  updatedByPhone?: string | null;
  updatedAt?: string | null;
}

export interface VerticalPerformanceItemDTO {
  vertical: string;
  verticalLabel: string;
  skuCount: number;
  unitsSold: number;
  totalVolumeKgOrLitre: number;
  measurementBasis: string;
  gmvPaise: number;
  vendorCostPaise: number;
  logisticsRevenuePaise: number;
  baselineMarginPaise: number;
  actualMarginPaise: number;
  marginVariancePaise: number;
}

export interface SkuMarginPerformanceItemDTO {
  skuId: number;
  skuCode: string;
  skuName: string;
  category?: string | null;
  pricingVertical: string;
  slabLabel: string;
  vendorId?: number | null;
  vendorName?: string | null;
  weightKg?: number | null;
  unit?: string | null;
  vendorPricePaise: number;
  baselineLogisticsPaise: number;
  baselineMarginPaise: number;
  baselineSellingPricePaise: number;
  currentSellingPricePaise: number;
  actualMarginPaise: number;
  marginVariancePaise: number;
  adminPriceOverride: boolean;
  unitsSold: number;
  realizedLogisticsRevenuePaise: number;
  realizedActualMarginPaise: number;
  realizedBaselineMarginPaise: number;
}

export interface VendorOutstandingItemDTO {
  vendorId: number;
  vendorName: string;
  phone: string;
  grossOrderSupplyPaise: number;
  returnsDeductionPaise: number;
  paidAmountPaise: number;
  scheduledUnpaidPaise: number;
  totalOutstandingPaise: number;
  pendingPayoutCount: number;
}

export interface CompanyPerformanceDTO {
  totalGmvPaise: number;
  totalVendorCostPaise: number;
  totalLogisticsRevenuePaise: number;
  totalBaselineMarginPaise: number;
  totalActualMarginPaise: number;
  netMarginVariancePaise: number;
  totalPlatformTakePaise: number;
  totalVendorPayoutScheduledPaise: number;
  totalVendorPayoutProcessedPaise: number;
  totalVendorOutstandingPaise: number;
  totalReturnsDeductionPaise: number;
  totalOrdersCount: number;
  totalUnitsSold: number;
  totalActiveSkus: number;
  skusAboveBaselineCount: number;
  skusBelowBaselineCount: number;
  skusAtBaselineCount: number;
  verticalPerformance: VerticalPerformanceItemDTO[];
  skuMarginTracker: SkuMarginPerformanceItemDTO[];
  vendorOutstandings: VendorOutstandingItemDTO[];
}




